import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";

import Button from "components/common/Button";
import DragAndDrop from "components/common/DragAndDrop";
import { AlertType } from "components/common/Snackbar";
import SelectedFile from "components/common/DragAndDrop/SelectedFile";
import Modal from "components/common/Modal";

import {
  getUploadDocumentSignedUrlForExistingEmployee,
  saveDocumentsOfEmployee,
} from "services/BambooHrTabs/documents";
import { showSnackbar } from "store/actions/Utility";

import { MAX_FILE_SIZE_LIMIT, MAX_FILE_SIZE_LIMIT_TEXT } from "constants";

import "../Document.scss";
import DocumentModule from "./DocumentModule.enum";

const MAX_FILE_UPLOAD_COUNT = 10;
const SUPPORTED_FORMATS = ["application/pdf", "image/png", "image/jpeg"];

function DocumentModal({
  type,
  open,
  modalData,
  documentType,
  fetchEmployeeDocumentList,
  module = DocumentModule.EMPLOYEE_DOCUMENTS,
  onGettingSignedUrl,
  onSaveClicked,
  onClose,
  maxFileSizeLimit = MAX_FILE_SIZE_LIMIT,
  maxFileSizeLimitText = MAX_FILE_SIZE_LIMIT_TEXT,
  maxFileUploadCount = MAX_FILE_UPLOAD_COUNT,
}) {
  const dispatch = useDispatch();
  const params = useParams();

  const [isSaving, setIsSaving] = useState(false);
  const [files, setFiles] = useState([]);

  const handleFileUpload = (selectedFiles) => {
    selectedFiles.map(async (fileMetaData) => {
      const { file } = fileMetaData;
      const unixTimeStamp = Math.floor(Date.now() / 1000);
      const newFileName = `${unixTimeStamp}_${file.name}`;

      let response = null;
      try {
        if (module === DocumentModule.EMPLOYEE_DOCUMENTS) {
          response = await getUploadDocumentSignedUrlForExistingEmployee(
            newFileName,
            file.type,
            file.size,
            documentType,
            params.id
          );
        } else if (module === DocumentModule.LEAVE_DOCUMENTS) {
          response = await onGettingSignedUrl(newFileName, file.type, file.size);
        }
      } catch (error) {
        dispatch(
          showSnackbar({
            type: AlertType.error,
            message: error?.message,
            dispatch,
          })
        );
      }

      try {
        const options = {
          headers: {
            "Content-Type": file.type,
          },
          onUploadProgress: (progressEvent) => {
            const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setFiles((prev) =>
              prev.map((item) =>
                item.id === fileMetaData.id
                  ? { ...item, retry: false, status: "uploading", error: false, progress: percentage }
                  : item
              )
            );
          },
        };

        await axios.put(response?.url, file, options);

        setFiles((prev) =>
          prev.map((item) =>
            item.id === fileMetaData.id ? { ...item, uniqueFileName: newFileName, status: "complete" } : item
          )
        );
      } catch (error) {
        setFiles((prev) =>
          prev.map((item) =>
            item.id === fileMetaData.id ? { ...item, status: "failed", error: "Network error", retry: true } : item
          )
        );

        dispatch(
          showSnackbar({
            type: AlertType.error,
            message: error?.message,
            dispatch,
          })
        );
      }
    });
  };

  const handleCloseModal = () => {
    setFiles([]);
    onClose();
  };

  const isSaveButtonDisabled = () => {
    const hasCompleteFile = files.some((file) => file.status === "complete");
    const hasUploadingFile = files.some((file) => file.status === "uploading");

    if (hasCompleteFile && !hasUploadingFile) {
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    const apiPayload = files
      .map((fileData) => {
        const { uniqueFileName = "", file = {}, status } = fileData || {};
        const { type: fileType, size } = file || {};

        if (status === "complete")
          return {
            fileName: uniqueFileName,
            documentName: fileData.fileName,
            fileType,
            size,
            documentType,
          };
        return null;
      })
      .filter(Boolean);

    setIsSaving(true);

    try {
      await saveDocumentsOfEmployee(params.id, { files: apiPayload });

      dispatch(showSnackbar({ type: AlertType.success, message: "Document added successfully", dispatch }));
    } catch (error) {
      dispatch(showSnackbar({ type: AlertType.error, message: error?.message, dispatch }));
    } finally {
      fetchEmployeeDocumentList();
      setIsSaving(false);
      handleCloseModal();
    }
  };

  return (
    <Modal
      width="652px"
      title={type === "add" ? "Add Documents" : "Edit Documents"}
      open={open}
      onClose={handleCloseModal}
    >
      <div className="para-body-m-regular">{documentType}</div>
      <div className="my-1">
        {modalData ? (
          <SelectedFile
            files={[
              {
                id: 1,
                fileName: `${modalData?.documentName}${modalData?.documentExtension}`,
                error: "",
                size: modalData?.size,
              },
            ]}
            loading={false}
            showGreenTick={false}
            showButtons={false}
          />
        ) : (
          <DragAndDrop
            loading={false}
            helperText={`PDF, PNG, or JPG ${maxFileSizeLimitText} ${
              maxFileUploadCount ? `• Max ${maxFileUploadCount} documents allowed` : ""
            } `}
            supportedFormats={SUPPORTED_FORMATS}
            maxFileSize={maxFileSizeLimit}
            onFileSelect={handleFileUpload}
            isMultiFileSupported
            files={files}
            setFiles={setFiles}
            maximumFilesCount={maxFileUploadCount}
          />
        )}
      </div>

      <div className="flex flex-row justify-end">
        <Button label="Cancel" variant="outlined" onClick={handleCloseModal} size="large" />
        <div className="ml-05">
          <Button
            label="Save"
            isDisabled={isSaveButtonDisabled()}
            onClick={() => {
              if (onSaveClicked) {
                onSaveClicked(files);
                setFiles([]);
              } else {
                handleSave();
              }
            }}
            isLoading={isSaving}
            size="large"
          />
        </div>
      </div>
    </Modal>
  );
}

export default DocumentModal;
